import { CONSENTABLE_CONTENT_VERSION } from './constants'

import noop from 'utils/noop'

import {
  clearTCFStore,
  getACString,
  getTCFConsentableContentVersion,
  getTCString,
  saveTCFSettings,
} from 'utils/tcfStore'

import applyConsentStringsToDOM from './applyConsentStringsToDOM'
import getGVLMetadata from './getGVLMetadata'
import installErrorCmpApi from './installErrorCmpApi'
import installTCFMutationObserver from './installTCFMutationObserver'
import makeIsDomainHandledByTCF from './makeIsDomainHandledByTCF'

const IS_SHOWING_UI_TO_USER = true

// Stands in for the real CMP API when its chunk never arrives, so that every
// method below settles instead of rejecting.
const NOOP_CMP_API = {
  disable: noop,
  update: noop,
}


// This function should return only the data and functions that are required
// to manipulate TCF consents outside the context of a React component or hook
// (e.g., in auto-blocker). TCFProvider should be used for TCF shenanigans inside
// of those contexts.
//
export default function initTCF() {
  const gvlMetadata = getGVLMetadata()

  if ( !gvlMetadata ) {
    return
  }

  // Nothing in the eager graph may import the IAB libraries, so the CMP API
  // arrives through an import(). Waiting until something actually needs it
  // would put that request behind whatever event triggered the need, so we
  // kick it off here and let it land while the rest of the page loads.
  // Vendors are covered in the meantime: the backend serves the IAB stub
  // ahead of this bundle, and the CMP API drains the stub's queue on arrival.
  const cmpApiPromise = import('utils/tcf-iab')
    .then(({ makeCmpApi }) => makeCmpApi())
    .catch((error) => {
      console.error('[Termly] The IAB TCF libraries failed to load, so the CMP API was never '
                    + 'installed. window.__tcfapi() now reports cmpStatus "error" so that '
                    + 'vendors stop waiting on us.\n\n%s',
                    error)

      installErrorCmpApi()

      return NOOP_CMP_API
    })

  // We call this no matter what happens to the chunk above, because it does
  // not need the IAB libraries. It reads the consent strings straight out of
  // localStorage and interpolates them into image URLs, so a returning
  // visitor's stored consent keeps reaching pixels even when the CMP API
  // never arrives. Waiting on the promise would also let any image injected
  // in the meantime slip past unmodified.
  installTCFMutationObserver()

  // Apply consent strings to existing DOM elements if we have them in localStorage.
  // This ensures that tracking pixels with macros (e.g., gdpr_consent=${GDPR_CONSENT})
  // get properly replaced on page reload, not just when consent is first granted.
  const tcString = getTCString()
  if ( tcString ) {
    const acString = getACString()

    applyConsentStringsToDOM({
      acString,
      tcString,
    })
  }

  const isConsentableContentVersionChanged = ( CONSENTABLE_CONTENT_VERSION !== getTCFConsentableContentVersion() )

  const isDomainHandledByTCF = makeIsDomainHandledByTCF(gvlMetadata.tcf_domains)

  return {
    ccVersion: CONSENTABLE_CONTENT_VERSION,
    getACString,
    getTCString,
    isConsentableContentVersionChanged,
    isDomainHandledByTCF,

    async disable() {
      const cmpApi = await cmpApiPromise

      cmpApi.disable()
    },

    async expireConsents() {
      // The store is cleared before the await because the caller shows the
      // banner on the very next line, and the banner reads the stored strings
      // as it builds its model.
      clearTCFStore()

      const cmpApi = await cmpApiPromise

      // We show the UI to the user to prompt him for fresh consent
      cmpApi.update('', IS_SHOWING_UI_TO_USER)
    },

    async setTCFSettings(settings) {
      saveTCFSettings(settings)

      // The stored string is read before the await because `expireConsents()`
      // can clear it while the chunk is still on the wire, and this call should
      // publish whatever was current when it was made.
      const tcString = getTCString()

      const cmpApi = await cmpApiPromise

      // This is basically just to trigger window.__tcfapi() to let vendors
      // (and the CMP Validator) know that everything's all setup. If we omit
      // this step, the CMP validator will not work.
      cmpApi.update(tcString)
    },

    async updateCmpApi(tcString) {
      const cmpApi = await cmpApiPromise

      cmpApi.update(tcString)
    },
  }
}
